import React, { useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { Share2, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

export const SettingsConnectedAccountsCard: React.FC = () => {
  const { user } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const googleAccount = user?.externalAccounts?.find(
    (acc) => acc.provider === 'google' || acc.verification?.strategy === 'oauth_google'
  );

  const handleConnectGoogle = async () => {
    if (!user) return;
    setIsLoading(true);
    setError(null);

    try {
      const externalAccount = await user.createExternalAccount({
        strategy: 'oauth_google',
        redirectUrl: window.location.href,
      });

      const reauthUrl = externalAccount.verification?.externalVerificationRedirectURL;
      if (reauthUrl) {
        window.location.href = reauthUrl.toString();
      }
    } catch (err: any) {
      setError(err?.errors?.[0]?.message || err?.message || 'Failed to initiate Google authentication.');
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-[#0E1015] border border-zinc-800 rounded-2xl p-6">
      <div className="flex items-center gap-3 pb-4 border-b border-zinc-800 mb-6">
        <div className="w-8 h-8 rounded-lg bg-indigo-600/10 border border-indigo-500/20 flex items-center justify-center text-indigo-400">
          <Share2 className="w-4 h-4" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-white">Connected Accounts</h2>
          <p className="text-xs text-zinc-400">Link single sign-on accounts for fast, seamless access</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex items-center justify-between p-4 rounded-xl bg-zinc-950 border border-zinc-850">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/5 border border-zinc-800 flex items-center justify-center">
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
              />
            </svg>
          </div>
          <div>
            <p className="text-xs font-semibold text-white">Google Account</p>
            <p className="text-[11px] text-zinc-400">
              {googleAccount ? googleAccount.emailAddress : 'Connect for one-click authentication'}
            </p>
          </div>
        </div>

        {googleAccount ? (
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium">
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>Connected</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={handleConnectGoogle}
            disabled={isLoading}
            className="px-3.5 py-1.5 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-white rounded-xl text-xs font-medium transition cursor-pointer flex items-center gap-1.5"
          >
            {isLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            <span>Connect</span>
          </button>
        )}
      </div>
    </div>
  );
};
